// brandAvailabilityHint — derives a one-line "heads up" message for
// a brand based on its retail-channel + region + exclusivity metadata.
//
// Triggers (in priority order, first match wins):
//
//   1. exclusive_to is set
//      → "Only at {Costco}."
//
//   2. retail_channels is online-only / dtc-only
//      → "Online only — try Amazon, Instacart, or {brand}.com."
//
//   3. retail_channels excludes mass + grocery (specialty / natural only)
//      → "Usually only at specialty + natural-foods stores."
//
//   4. scope is local/regional AND user's region isn't in regions
//      → "Mostly stocked in {regions}. Likely not at your local stores."
//
//   5. stores has no overlap with user's preferred_stores (when set)
//      → "Your usual stores don't carry it — try {nearest known store}."
//
// When none of the above fire, returns null. The banner only surfaces
// when there's something useful to warn about; mainstream brands stay
// quiet.
//
// All inputs come from the brand record + user prefs (no Supabase round
// trip). Computed at render time on the BrandStep — pure function.

const MAINSTREAM_CHANNELS = new Set([
  "mass", "grocery", "club", "convenience",
]);
const ONLINE_ONLY_CHANNELS = new Set([
  "online", "dtc", "amazon", "instacart",
]);

/**
 * @param {object} brand — record from brandsForCanonical()
 *   { name, scope, regions, stores, retail_channels, exclusive_to, ... }
 * @param {object} ctx
 *   region          — user's shopping region (e.g. "us-midwest-chicago")
 *   preferredStores — array of store names the user shops at (optional)
 * @returns {{ kind, message } | null}
 */
export function brandAvailabilityHint(brand, ctx = {}) {
  if (!brand) return null;
  const { region = null, preferredStores = null } = ctx;

  // 1. Retailer-exclusive (private label / Costco-only / TJ-only).
  if (typeof brand.exclusive_to === "string" && brand.exclusive_to.trim()) {
    return {
      kind: "exclusive",
      message: `Only at ${brand.exclusive_to.trim()}.`,
    };
  }

  // 2. Online-only / DTC-only.
  const channels = Array.isArray(brand.retail_channels) ? brand.retail_channels : [];
  if (channels.length > 0) {
    const allOnline = channels.every(c => ONLINE_ONLY_CHANNELS.has(c));
    if (allOnline) {
      return {
        kind: "online-only",
        message: `Online only — try Amazon, Instacart, or ${brand.name}.com.`,
      };
    }
    // 3. Specialty / natural only (no mass/grocery exposure).
    const hasMainstream = channels.some(c => MAINSTREAM_CHANNELS.has(c));
    if (!hasMainstream) {
      const labels = channels
        .filter(c => c === "specialty" || c === "natural")
        .map(c => c === "specialty" ? "specialty grocers" : "natural-foods stores");
      const list = labels.length > 0 ? labels.join(" + ") : "specialty stores";
      return {
        kind: "specialty",
        message: `Usually only at ${list}.`,
      };
    }
  }

  // 4. Region-locked AND user is outside the brand's footprint.
  if (region && Array.isArray(brand.regions) && brand.regions.length > 0) {
    const matches = brandRegionMatches(brand.regions, region);
    if (!matches && (brand.scope === "regional" || brand.scope === "local" || brand.scope === "country-local")) {
      // Format the brand's regions for display ("us-midwest-chicago"
      // → "Chicago / Midwest US").
      const friendly = brand.regions.map(prettyRegion).join(", ");
      return {
        kind: "region-locked",
        message: `Mostly stocked in ${friendly}. Likely not at your local stores.`,
      };
    }
  }

  // 5. Brand's stores don't overlap with user's preferred stores.
  if (Array.isArray(preferredStores) && preferredStores.length > 0
      && Array.isArray(brand.stores) && brand.stores.length > 0) {
    const overlap = brand.stores.some(s => preferredStores.includes(s));
    if (!overlap) {
      const sample = brand.stores.slice(0, 3).join(", ");
      return {
        kind: "store-mismatch",
        message: `Your usual stores don't carry this — try ${sample}${brand.stores.length > 3 ? ", or others" : ""}.`,
      };
    }
  }

  return null;
}

// Hierarchical region match — "us-midwest" matches both "us" and
// "us-midwest-chicago". Identical to the helper in useShoppingRegion
// but inlined to keep this file dependency-free of React hooks.
function brandRegionMatches(regions, userRegion) {
  const u = String(userRegion || "").toLowerCase();
  for (const r of regions) {
    if (typeof r !== "string") continue;
    const b = r.toLowerCase();
    if (u === b) return true;
    if (u.startsWith(b + "-")) return true;
    if (b.startsWith(u + "-")) return true;
  }
  return false;
}

// Format region tag for display. "us-midwest-chicago" → "Chicago"
// (most specific component); "us-midwest" → "the Midwest"; "us" →
// "the US"; "de-bavaria-munich" → "Munich". Falls through to the
// raw tag for unrecognized formats.
function prettyRegion(tag) {
  if (typeof tag !== "string") return tag;
  const lower = tag.toLowerCase();
  const KNOWN_LABELS = {
    "us-northeast":      "the Northeast US",
    "us-midwest":        "the Midwest US",
    "us-south":          "the Southern US",
    "us-southeast":      "the Southeast US",
    "us-southwest":      "the Southwestern US",
    "us-west":           "the West Coast",
    "us-northwest":      "the Pacific Northwest",
    "us-midwest-chicago": "Chicago",
    "us-midwest-st-louis": "St. Louis",
    "us-midwest-detroit":  "Detroit",
    "us-northeast-nyc":    "NYC",
    "us-northeast-boston": "Boston",
    "us-south-texas":      "Texas",
    "us-south-louisiana":  "Louisiana",
    "us-west-la":          "LA",
    "us-west-sf":          "the Bay Area",
    "us-northwest-portland":  "Portland",
    "us-northwest-seattle":   "Seattle",
    "us":                "the US",
    "international":     "internationally",
  };
  if (KNOWN_LABELS[lower]) return KNOWN_LABELS[lower];
  // Fallback: humanize the tail of the slug.
  const parts = lower.split("-");
  const last = parts[parts.length - 1];
  return last.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
